import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle, Plus, Trash } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";

const STEPS = [
  { n: 1, code: "1", label: "Growth", phase: "OPERATIONS", field: "growth", ph: "Growth %", type: "number" },
  { n: 2, code: "2", label: "Share", phase: "OPERATIONS", field: "share", ph: "Share %", type: "number" },
  { n: 3, code: "3", label: "Contribution", phase: "OPERATIONS", field: "margin", ph: "Margin / revenue", type: "text" },
  { n: 4, code: "4", label: "Resource Use", phase: "OPERATIONS", field: "consumption", ph: "Resource / headcount consumption", type: "text" },
];
const LAST = 4;
const uid = () => "as_" + Math.random().toString(36).slice(2, 10);
const inputCls = "flex h-10 rounded-sm border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

export default function AssetLifecycle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/documents/${id}`); setTitle(res.data.title || ""); setAssets(res.data.payload?.assets || []); }
      catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id]); // eslint-disable-line

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled Lifecycle Matrix", payload: { assets, growthThreshold: 10, shareThreshold: 50 }, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, assets]);

  const cur = STEPS[step - 1];
  const addAsset = () => setAssets((x) => [...x, { id: uid(), name: "", growth: "", share: "", margin: "", consumption: "" }]);
  const setAsset = (i, patch) => setAssets((x) => x.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const removeAsset = (i) => setAssets((x) => x.filter((_, j) => j !== i));
  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => { if (await persist({ status: "Completed" })) { toast.success("Matrix finalised"); navigate(`/lifecycle/${id}/view`); } };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="lifecycle-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Lifecycle matrix title…" className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />
        <div className="mt-5 grid grid-cols-4 gap-2" data-testid="lifecycle-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)} className={`rounded-sm border px-2 py-2.5 text-center transition-all ${step === s.n ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-xs font-bold block ${step === s.n ? "text-blue-700" : "text-slate-400"}`}>{s.n}</span>
              <p className={`text-[11px] font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>
        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between"><h2 className="text-xl font-semibold text-slate-900">{cur.label}</h2>{step === 1 && <button data-testid="lifecycle-add-asset" onClick={addAsset} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><Plus size={16} /> Add asset</button>}</div>
          {assets.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Add product lines or assets in Step 1.</p>}
          <div className="space-y-2">
            {assets.map((a, i) => (
              <div key={a.id} data-testid={`asset-row-${i}`} className="flex items-center gap-2">
                {step === 1 ? (
                  <input data-testid={`asset-name-${i}`} className={inputCls + " flex-1"} value={a.name} onChange={(e) => setAsset(i, { name: e.target.value })} placeholder="Asset / product line" />
                ) : (
                  <span className="flex-1 text-sm text-slate-800">{a.name || <span className="text-slate-300">Unnamed</span>}</span>
                )}
                <input data-testid={`asset-${cur.field}-${i}`} type={cur.type} className={inputCls + " w-44"} value={a[cur.field]} onChange={(e) => setAsset(i, { [cur.field]: cur.type === "number" ? e.target.value : e.target.value })} placeholder={cur.ph} />
                {step === 1 && <button data-testid={`asset-remove-${i}`} onClick={() => removeAsset(i)} className="p-2 text-slate-400 hover:text-red-600"><Trash size={16} /></button>}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="lifecycle-back" onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3">
            <button data-testid="lifecycle-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
            {step < LAST ? (
              <button data-testid="lifecycle-next" onClick={next} className="inline-flex items-center gap-2 rounded-sm bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800">Next <ArrowRight size={16} /></button>
            ) : (
              <button data-testid="lifecycle-finalise" onClick={finalise} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"><CheckCircle size={18} weight="fill" /> Finalise Matrix</button>
            )}
          </div>
        </div>
      </div>
      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase={cur.phase} />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="asset_lifecycle" step={cur} phase={cur.phase} onApplySuggestion={(t) => { navigator.clipboard?.writeText(t); toast.info("Copied to clipboard"); }} />
    </Layout>
  );
}
